/**
 * Capability 1: Consumer Intelligence
 * Purpose: Read-only intelligence about consumers.
 */
import { useEffect, useState } from "react";
import CapabilityLandingPage from "@/components/capability/CapabilityLandingPage";
import api from "@/lib/api";
import {
  Users, Route, Radio, Activity, IdCard, Brain, ArrowUpRight,
} from "lucide-react";

export default function ConsumerIntelligenceCapability() {
  const [kpis, setKpis] = useState({ consumers: null, journeys: null, signals: null, profiles: null });

  useEffect(() => {
    (async () => {
      const next = { consumers: null, journeys: null, signals: null, profiles: null };
      try { const { data } = await api.get("/consumers"); next.consumers = Array.isArray(data) ? data.length : null; } catch { /* endpoint optional */ }
      try { const { data } = await api.get("/journeys"); next.journeys = Array.isArray(data) ? data.length : (Array.isArray(data?.journeys) ? data.journeys.length : null); } catch { /* endpoint optional */ }
      try { const { data } = await api.get("/wallet-signals/summary"); next.signals = data?.total_signals_7d ?? null; } catch { /* endpoint optional */ }
      // Consumer Trust Profile is future — leave placeholder
      setKpis(next);
    })();
  }, []);

  return (
    <CapabilityLandingPage
      eyebrow="Capability 1 · Consumer Intelligence"
      title="Consumer Intelligence"
      purpose="Provides a unified, auditable view of every consumer and their trust journey. Read-only intelligence layer — every access is scoped, filtered by tenant, and logged."
      primaryActions={[
        { label: "Browse Consumer Register", to: "/consumers", icon: Users },
        { label: "View Consumer Journeys", to: "/journeys", icon: Route },
      ]}
      kpis={[
        { label: "Consumers", value: kpis.consumers, subLabel: "in this tenant" },
        { label: "Active journeys", value: kpis.journeys, subLabel: "across all products" },
        { label: "Trust signals (7d)", value: kpis.signals, subLabel: "wallet activity" },
        { label: "Trust profiles built", value: null, emptyHint: "Coming with Trust Profile" },
      ]}
      subCapabilities={[
        { name: "Consumer Register", to: "/consumers", icon: Users, status: "live",
          description: "Every consumer known to this tenant with a unified profile." },
        { name: "Consumer Journeys", to: "/journeys", icon: Route, status: "live",
          description: "Product journeys with progress, decision state, and evidence." },
        { name: "Trust Signals", to: "/wallet-intelligence", icon: Radio, status: "live",
          description: "Aggregated wallet-side behavioural intelligence." },
        { name: "Timeline", to: "/timeline", icon: Activity, status: "live",
          description: "Chronological event feed across every consumer touchpoint." },
        { name: "Consumer Trust Profile", to: "/consumer-trust-profile", icon: IdCard, status: "future",
          description: "Composite trust score, verifiable claims, portable identity." },
        { name: "Behaviour Insights", to: "/behaviour-insights", icon: Brain, status: "future",
          description: "Rule + LLM-driven patterns across financial life events." },
      ]}
      relatedCapabilities={[
        { name: "Governance · Evidence Vault", to: "/capability/governance" },
        { name: "Trust Workflows", to: "/capability/trust-workflows" },
        { name: "Institution Workspace", to: "/capability/institution-workspace" },
      ]}
    />
  );
}
